import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
    GET_LIST_OF_SERVICES_UNSAVED_DATA,
    GET_LIST_OF_SERVICES_UNSAVED_NUMBERS,
    LIST_OF_SERVICES_CHAPTER_ACTION,
} from '../queries.ts';
import useSearchParamState from '../../../core/utils/useSearchParamState.tsx';
import { unpackAndMergeDynamicSchemaFields } from '../../../core/utils/schema.ts';
import { IChapter, IPosition } from '../pages/Assistance.tsx';

/**
 * Pure function that applies patched positions to a chapter map, returning a new map.
 * Preserves `indexInChapter` from existing positions when not present in the patch.
 */
export const applyPatchedPositionsToChapterMap = (
    chapterMap: Map<string, IChapter>,
    patchedPositions: IPosition[]
): Map<string, IChapter> => {
    const updatedMap = new Map(chapterMap);

    patchedPositions?.forEach((position) => {
        const chapterNumber = position.chapter;
        const chapterData = updatedMap.get(chapterNumber);
        if (!chapterData) return;

        const existingIndex = chapterData.positions.findIndex((p) => p.positionIndex === position.positionIndex);

        if (existingIndex === -1) {
            return;
        }

        const updatedPosition = {
            ...position,
            indexInChapter: position.indexInChapter ?? chapterData.positions[existingIndex].indexInChapter,
        };
        const newPositions = [...chapterData.positions];
        newPositions[existingIndex] = updatedPosition;
        updatedMap.set(chapterNumber, { ...chapterData, positions: newPositions });
    });

    return updatedMap;
};

export const useChapterManagement = ({
    record,
    setRecord,
    chapterMap,
    setChapterMap,
}: {
    record: any;
    setRecord: any;
    chapterMap: Map<string, IChapter> | null;
    setChapterMap: React.Dispatch<React.SetStateAction<Map<string, IChapter> | null>>;
}) => {
    const [chapter, setChapter] = useState<string>('');
    const chapterBeingLoaded = useRef<string | undefined>(undefined);

    const [getPositions, { loading }] = useLazyQuery(GET_LIST_OF_SERVICES_UNSAVED_DATA, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });

    const [getNumbers] = useLazyQuery(GET_LIST_OF_SERVICES_UNSAVED_NUMBERS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });

    const [chapterAction] = useMutation(LIST_OF_SERVICES_CHAPTER_ACTION);
    const [isChapterActionLoading, setIsChapterActionLoading] = useState(false);

    const loadChapterAndSetPositionsOnRecord = useCallback(
        async (chapterNumber: string, currentChapterMap: Map<string, IChapter>) => {
            let positions = currentChapterMap.get(chapterNumber).positions;

            if (!positions.length) {
                chapterBeingLoaded.current = chapterNumber;

                setChapterMap((prevMap) => {
                    const updatedMap = new Map(prevMap);
                    const chapterData = updatedMap.get(chapterNumber);
                    if (chapterData) {
                        updatedMap.set(chapterNumber, { ...chapterData, isLoading: true });
                    }
                    return updatedMap;
                });

                const { data } = await getPositions({
                    variables: {
                        id: record.id,
                        chapter: chapterNumber,
                    },
                });

                positions = unpackAndMergeDynamicSchemaFields(
                    data?.listOfServicesProcessingRecord.listOfServicesUnsaved?.positions || []
                );

                chapterBeingLoaded.current = undefined;

                setChapterMap((prevMap) => {
                    const updatedMap = new Map(prevMap);
                    const chapterData = updatedMap.get(chapterNumber);
                    if (chapterData) {
                        updatedMap.set(chapterNumber, { ...chapterData, positions, isLoading: false });
                    }
                    return updatedMap;
                });
            }

            // Direct mutation is intentional here: useAssistanceHandlers.handleUpdate reads
            // record from its closure to apply JSON patches. It must see the latest positions
            // on the same object reference, even if the closure is stale.
            record.listOfServicesUnsaved.positions = positions;
            setRecord(record);
        },
        [record, getPositions, setRecord]
    );

    const handleChapterHeaderClick = useCallback(
        async ({ openChapter, chapter: clickedChapter }) => {
            if (openChapter) {
                setChapter(clickedChapter);
                await loadChapterAndSetPositionsOnRecord(clickedChapter, chapterMap);
            } else {
                setChapter('');
            }

            setChapterMap((prevMap) => {
                if (!prevMap) return prevMap;
                const updatedMap = new Map(prevMap);
                prevMap.forEach((chapterData, key) => {
                    updatedMap.set(key, {
                        ...chapterData,
                        isExpanded: key === clickedChapter ? openChapter : false,
                    });
                });
                return updatedMap;
            });
        },
        [chapterMap, setChapter, loadChapterAndSetPositionsOnRecord]
    );

    const updateChapterPositions = useCallback(
        async (chapterNumber: string) => {
            const { data } = await getPositions({
                variables: {
                    id: record.id,
                    chapter: chapterNumber,
                },
            });

            const positions = unpackAndMergeDynamicSchemaFields(
                data?.listOfServicesProcessingRecord.listOfServicesUnsaved?.positions || []
            );
            const chapters = data?.listOfServicesProcessingRecord?.listOfServicesUnsaved?.chapters;

            setChapterMap((prevMap) => {
                const updatedMap = new Map(prevMap);
                const chapterData = updatedMap.get(chapterNumber);
                if (chapterData) {
                    updatedMap.set(chapterNumber, {
                        ...chapterData,
                        positions,
                    });
                }
                return updatedMap;
            });

            if (record?.listOfServicesUnsaved) {
                record.listOfServicesUnsaved.positions = positions;
                if (chapters) {
                    record.listOfServicesUnsaved.chapters = chapters;
                }
                setRecord(record);
            }
        },
        [record, getPositions, setRecord]
    );

    const handleMarkAllRelevant = useCallback(
        async ({ chapter, subchapter }: { chapter: string; subchapter?: string }) => {
            setIsChapterActionLoading(true);
            await chapterAction({
                variables: {
                    recordId: record.id,
                    chapter,
                    subchapter,
                    action: 'mark_all_relevant',
                },
            });
            await updateChapterPositionsAndNumbers(chapter);

            setIsChapterActionLoading(false);
        },
        [record, chapterAction, updateChapterPositions]
    );

    const handleMarkAllNotRelevant = useCallback(
        async ({ chapter, subchapter }: { chapter: string; subchapter?: string }) => {
            setIsChapterActionLoading(true);
            await chapterAction({
                variables: {
                    recordId: record.id,
                    chapter,
                    subchapter,
                    action: 'mark_all_not_relevant',
                },
            });
            await updateChapterPositionsAndNumbers(chapter);
            setIsChapterActionLoading(false);
        },
        [record, chapterAction, updateChapterPositions]
    );

    const updateChapterPositionsAndNumbers = async (chapter) => {
        await updateChapterPositions(chapter);
    };

    const totals = useMemo(() => {
        if (!chapterMap) return { numberTotal: 0, numberRelevant: 0, numberRelevantCompleted: 0 };

        let total = {
            numberTotal: 0,
            numberRelevant: 0,
            numberRelevantCompleted: 0,
        };

        for (const [, chapter] of chapterMap) {
            if (chapter.positions?.length > 0) {
                for (const p of chapter.positions) {
                    if (p.prediction === 'PreText') continue;
                    total.numberTotal++;
                    if (p.isRelevant?.value === 'True') {
                        total.numberRelevant++;
                        const isComplete = !p.articleSets?.length || p.articleSets.every((s) => s.isComplete);
                        if (isComplete) total.numberRelevantCompleted++;
                    }
                }
            } else {
                total.numberTotal += chapter.numberTotal || 0;
                total.numberRelevant += chapter.numberRelevant || 0;
                total.numberRelevantCompleted += chapter.numberCompleted || 0;
            }
        }

        return total;
    }, [chapterMap]);

    return {
        chapter,
        setChapter,
        loading,
        chapterBeingLoaded: chapterBeingLoaded.current,
        isChapterActionLoading,
        loadChapterAndSetPositionsOnRecord,
        handleChapterHeaderClick,
        handleMarkAllRelevant,
        handleMarkAllNotRelevant,
        updateChapterPositions,
        totals,
    };
};
